/**
 * Workflows executions list.
 *
 * Operator surface for recent workflow runs. Reads from
 *   GET /workflows/executions?search=&status=&workflow_key=&project_id=&sort_by=&sort_order=&limit=&offset=
 *
 * Uses the standard server-paginated grid (usePaginatedData + SearchInput +
 * SortableTableHead + Pagination): every page/search/sort/filter round-trips
 * to the backend, so the table never client-slices a capped window. Filters
 * stay URL search params so a Run-now "View →" link can deep-link straight to
 * a filtered list (or an open run); page, sort and the search term are local
 * grid state (the hook owns sort; the term is debounced before it reaches the
 * hook's dependencies).
 *
 * Layout (patterns-ui § Standard Data Grid Page Layout):
 *   - One content panel: controls bar (search, Workflow + Project selects,
 *     Refresh), then the status chips (clickable; "All" clears) as a second
 *     row. Counts come from ``GET /executions/counts``, server-scoped to the
 *     active search/workflow/project. Fetched once per filter change (and on
 *     Refresh) — never on a page turn, sort or status-chip click, which do not
 *     change them (Supernova Check List row 19: the list used to recount the
 *     org's whole history on every call).
 *   - Workflow options come from ``GET /definitions/keys`` — one entry per
 *     WorkflowKey, no DSL — never the full versioned definitions list.
 *   - Sortable table: started / workflow / project / status / completed +
 *     error preview, with a Pagination footer.
 *   - Row click — or ?open=<id> on load — opens a drawer with the full
 *     error + node outputs from GET /executions/{id}.
 *
 * Filename: trailing-underscore so the route doesn't inherit
 * workflows.tsx as a layout — matches workflows_.subscriptions /
 * workflows_.dlq / workflows_.topology.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FlaskConical,
  HistoryIcon,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  XCircle,
} from "lucide-react";

import { ScrollableTable } from "@/components/ui/scrollable-table";
import { Button } from "@/components/ui/button";
import { DryRunResult } from "@/components/portal/workflow-flow-v2/DryRunResult";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { PageHeader } from "@/components/ui/page-header";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useAuth } from "@/contexts/AuthContext";
import { useDebounce } from "@/hooks/useDebounce";
import {
  usePaginatedData,
  type PaginatedResponse,
  type ServerSortState,
} from "@/hooks/usePaginatedData";
import { readSelectedOrganisationId } from "@/lib/selectedOrganisationStorage";
import { toast } from "sonner";
import {
  workflowsApi,
  type DefinitionKeyResponse,
  type ExecutionListStatus,
  type ExecutionResponse,
  type ExecutionSortField,
  type ExecutionSummary,
  type WorkflowProject,
} from "@/services/api/workflows";

/** URL-driven filters — every filter is a search param so a run's "View →"
 *  link can deep-link straight to a filtered list (or an open drawer). */
interface ExecutionsSearch {
  status?: ExecutionListStatus;
  workflow?: string;
  project?: string;
  /** Execution id to auto-open in the detail drawer on load. */
  open?: string;
}

const _STATUSES: ExecutionListStatus[] = [
  "running",
  "suspended",
  "completed",
  "completed_with_errors",
  "failed",
];

/** The org the API client sends as X-Platform-Organisation-Id (portalApi reads
 *  the same value per request). */
function storedOrganisationId(): string | undefined {
  return readSelectedOrganisationId() ?? undefined;
}

export const Route = createFileRoute("/portal/workflows/executions")({
  validateSearch: (search: Record<string, unknown>): ExecutionsSearch => ({
    status:
      typeof search.status === "string" &&
      (_STATUSES as string[]).includes(search.status)
        ? (search.status as ExecutionListStatus)
        : undefined,
    workflow:
      typeof search.workflow === "string" && search.workflow
        ? search.workflow
        : undefined,
    project:
      typeof search.project === "string" && search.project
        ? search.project
        : undefined,
    open:
      typeof search.open === "string" && search.open ? search.open : undefined,
  }),
  component: ExecutionsPage,
});

type StatusFilter = ExecutionListStatus | "all";

const STATUS_ORDER: ExecutionListStatus[] = [
  "running",
  "suspended",
  "completed",
  "completed_with_errors",
  "failed",
];

const STATUS_LABEL: Record<ExecutionListStatus, string> = {
  running: "Running",
  suspended: "Suspended",
  completed: "Completed",
  completed_with_errors: "Completed with errors",
  failed: "Failed",
};

const STATUS_ICON: Record<ExecutionListStatus, typeof CheckCircle2> = {
  running: Play,
  suspended: Pause,
  completed: CheckCircle2,
  completed_with_errors: AlertTriangle,
  failed: XCircle,
};

const STATUS_CHIP_CLASS: Record<ExecutionListStatus, string> = {
  running: "bg-blue-500/10 text-blue-700 border-blue-500/30 hover:bg-blue-500/20",
  suspended:
    "bg-amber-500/10 text-amber-700 border-amber-500/30 hover:bg-amber-500/20",
  completed:
    "bg-emerald-500/10 text-emerald-700 border-emerald-500/30 hover:bg-emerald-500/20",
  completed_with_errors:
    "bg-amber-500/10 text-amber-700 border-amber-500/30 hover:bg-amber-500/20",
  failed: "bg-rose-500/10 text-rose-700 border-rose-500/30 hover:bg-rose-500/20",
};

function ExecutionsPage() {
  const { token } = useAuth();
  // The org rides on every request as a header read at request time, and an
  // org switch keeps this page mounted — so it keys the counts memo and the
  // refetch, or the chips would keep the previous org's numbers.
  // Until the org list loads, the context has no selection yet — but the
  // request header is read from the same storage key, so key on that and a
  // cold load fetches once, not again when the list arrives.
  const { selectedOrganisation } = usePortalData();
  const orgId = selectedOrganisation?.organisationId ?? storedOrganisationId();
  const navigate = useNavigate();
  const { status: statusParam, workflow, project, open } = Route.useSearch();
  const filter: StatusFilter = statusParam ?? "all";

  // Free-text search is component state (URL filters are the deep-linkable
  // ones). Debounced before it reaches the hook's dependencies so a pause —
  // not every keystroke — resets to page 1 and refetches once (D12).
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);
  // Per-status counts for the chips — server-scoped to the active
  // search/workflow/project filter, from GET /executions/counts.
  // undefined = still counting, null = the count failed; either way the rows
  // show and the chips read "—".
  const [counts, setCounts] = useState<
    Partial<Record<ExecutionListStatus, number>> | null | undefined
  >(undefined);

  const [detail, setDetail] = useState<ExecutionResponse | null>(null);
  const [detailFor, setDetailFor] = useState<ExecutionSummary | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // "Re-run as test" — replay this run as a dry-run against the current
  // published version (WS6). Reset whenever the drawer target changes.
  const [rerunResult, setRerunResult] = useState<ExecutionResponse | null>(null);
  const [rerunning, setRerunning] = useState(false);
  const [rerunError, setRerunError] = useState<string | null>(null);
  // Filter dropdown options — the org's full workflow-key + project sets, so
  // you can filter to something even if it has no run on the current page.
  // Keys come from /definitions/keys (one per WorkflowKey, no DSL) — the full
  // definitions list carries every version's DSL and is never loaded here.
  const [workflowKeys, setWorkflowKeys] = useState<DefinitionKeyResponse[]>([]);
  const [projects, setProjects] = useState<WorkflowProject[]>([]);

  /** Merge a filter change into the URL search params (history-replace so the
   *  back button doesn't step through every filter tweak). */
  const setSearch = useCallback(
    (patch: Partial<ExecutionsSearch>) => {
      void navigate({
        to: "/portal/workflows/executions",
        search: (prev) => ({ ...prev, ...patch }),
        replace: true,
      });
    },
    [navigate],
  );

  // Monotonic fetch id so a superseded (late-resolving) request can't overwrite
  // the chip counts with stale-scoped numbers after a fast filter flip — the
  // hook already discards a stale response's rows/totalCount, but setCounts is
  // our own side-effect and needs the same guard.
  const fetchSeqRef = useRef(0);

  // The counts for the current search/workflow/project, memoised by that key.
  // Page turns, sorts and status-chip clicks reuse the in-flight or settled
  // promise; only a change to those three filters, the org, or Refresh
  // recounts. A failed count stays memoised for its key, so page turns do not
  // re-issue a count that just timed out; Refresh retries it.
  const countsMemoRef = useRef<{
    key: string;
    promise: Promise<Partial<Record<ExecutionListStatus, number>>>;
  } | null>(null);
  const loadCounts = useCallback(
    (search: string | undefined) => {
      const key = JSON.stringify([
        orgId ?? "",
        search ?? "",
        workflow ?? "",
        project ?? "",
      ]);
      const memo = countsMemoRef.current;
      if (memo && memo.key === key) return memo.promise;
      const promise = workflowsApi
        .countExecutions(token ?? undefined, {
          search,
          workflowKey: workflow,
          projectId: project,
        })
        .then((r) => r.counts_by_status);
      countsMemoRef.current = { key, promise };
      // Handled here so a count that fails before the list lands is not an
      // unhandled rejection; fetchExecutions applies the outcome later.
      promise.catch(() => {});
      setCounts(undefined);
      return promise;
    },
    [token, orgId, workflow, project],
  );

  // Server-paginated fetch: the page of rows, plus the (memoised) counts the
  // chips show and the pager's totalCount derives from (per-status count when
  // a status filter is active, else the sum — both server-scoped to
  // search/workflow/project, so the pager and the chips agree with the rows).
  // Sort comes from the hook.
  const fetchExecutions = useCallback(
    async (
      page: number,
      pageSize: number,
      sort?: ServerSortState,
    ): Promise<PaginatedResponse<ExecutionSummary>> => {
      const seq = ++fetchSeqRef.current;
      const search = debouncedSearch.trim() || undefined;
      // Counts are decoration: the rows never wait for them. A search count
      // scans the whole history (up to the 60s cap) while the page stops at
      // pageSize rows, so the chips and the pager total fill in when the
      // count lands — or read "—" if it fails. The count STARTS now, alongside
      // the list, but is APPLIED only once this fetch's rows have landed:
      // until then the previous scope's rows are on screen, and the new
      // scope's numbers must not sit over them.
      const countsPromise = loadCounts(search);
      // Applied in `finally` so a FAILED list still settles the chips: the
      // hook clears the rows on error, so no old scope's rows sit under them,
      // and a count that also failed shows the unavailable note instead of a
      // permanent "—".
      const applyCounts = () =>
        countsPromise.then(
          (c) => {
            if (seq === fetchSeqRef.current) setCounts(c);
          },
          () => {
            if (seq === fetchSeqRef.current) setCounts(null);
          },
        );
      let res;
      try {
        res = await workflowsApi.listExecutions(token ?? undefined, {
          search,
          status: filter === "all" ? undefined : filter,
          workflowKey: workflow,
          projectId: project,
          sortBy: sort?.key as ExecutionSortField | undefined,
          sortOrder: sort?.direction,
          limit: pageSize,
          offset: (page - 1) * pageSize,
        });
      } finally {
        void applyCounts();
      }
      // The pager's own total until the count lands (totalCountOverride
      // replaces it then): offer Next only when this page came back full.
      const totalCount =
        (page - 1) * pageSize +
        res.rows.length +
        (res.rows.length === pageSize ? 1 : 0);
      return { data: res.rows, totalCount, page, pageSize };
    },
    [token, debouncedSearch, filter, workflow, project, loadCounts],
  );

  // The true total, once the count has landed.
  const countedTotal =
    counts == null
      ? undefined
      : filter === "all"
        ? Object.values(counts).reduce((a, b) => a + (b ?? 0), 0)
        : (counts[filter] ?? 0);

  const {
    data: rows,
    isLoading,
    error,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
    requestSort,
    getSortDirection,
  } = usePaginatedData<ExecutionSummary>({
    fetchFn: fetchExecutions,
    initialPageSize: 25,
    initialSort: { key: "started_at", direction: "desc" },
    dependencies: [token, orgId, debouncedSearch, filter, workflow, project],
    totalCountOverride: countedTotal,
  });

  // The filter dropdown feeds — org-wide, fetched once on mount and on
  // Refresh (D11), never per page turn. Best-effort: a hiccup here leaves the
  // dropdowns short, it must not blank the list. Array-guarded because
  // `.catch()` only covers a rejected promise, not a 200 of the wrong shape.
  const loadFeeds = useCallback(async () => {
    if (!token) return;
    const [keys, projs] = await Promise.all([
      workflowsApi
        .listDefinitionKeys(token)
        .catch(() => [] as DefinitionKeyResponse[]),
      workflowsApi.listProjects(token).catch(() => [] as WorkflowProject[]),
    ]);
    setWorkflowKeys(Array.isArray(keys) ? keys : []);
    setProjects(Array.isArray(projs) ? projs : []);
  }, [token]);

  // orgId: the feeds are org-scoped, so an org switch reloads them.
  useEffect(() => {
    void loadFeeds();
  }, [loadFeeds, orgId]);

  /** Refresh = the feeds, fresh counts AND the current page, in that order. */
  const handleRefresh = useCallback(() => {
    void loadFeeds();
    countsMemoRef.current = null;
    refresh();
  }, [loadFeeds, refresh]);

  const openDetail = useCallback(
    async (row: ExecutionSummary) => {
      setDetailFor(row);
      setDetail(null);
      setRerunResult(null);
      setRerunError(null);
      setDetailLoading(true);
      try {
        const res = await workflowsApi.getExecution(row.id, token ?? undefined);
        setDetail(res);
      } catch (e) {
        const message = e instanceof Error ? e.message : "Failed to load run";
        toast.error(message);
        setDetailFor(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [token],
  );

  // Deep-link: ?open=<id> auto-opens that run's drawer once the list loads.
  // (The "View →" link from a Run-now lands here; the just-fired row is at the
  // top of the default newest-first list.) Guard on a ref of the handled id —
  // NOT on detailFor — so closing the drawer can't re-fire this and re-open it.
  const consumedOpenRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      consumedOpenRef.current = null;
      return;
    }
    // Don't consume the deep-link on a fetch ERROR (rows is [] but the run may
    // well exist) — a transient 5xx must not permanently strip ?open.
    if (isLoading || error || consumedOpenRef.current === open) return;
    consumedOpenRef.current = open;
    const row = rows.find((r) => r.id === open);
    if (row) {
      void openDetail(row);
    } else {
      // Run isn't on the loaded page — drop the dangling param rather than
      // leave a broken-looking link.
      setSearch({ open: undefined });
    }
  }, [open, rows, isLoading, error, openDetail, setSearch]);

  const closeDrawer = useCallback(() => {
    setDetailFor(null);
    setDetail(null);
    setRerunResult(null);
    setRerunError(null);
    if (open) setSearch({ open: undefined });
  }, [open, setSearch]);

  // Replay this run as a dry-run against the CURRENT published version — same
  // trigger + payload, but every external node simulated and nothing persisted.
  // The safe way to reproduce a failure and see where it breaks.
  const runReTest = useCallback(async () => {
    if (!detailFor?.workflow_key || !detailFor.trigger_id) return;
    setRerunning(true);
    setRerunError(null);
    setRerunResult(null);
    try {
      const snapshot = detail?.trigger_snapshot as
        | { payload?: Record<string, unknown> }
        | null
        | undefined;
      const res = await workflowsApi.runWorkflowByKey(
        detailFor.workflow_key,
        {
          trigger_id: detailFor.trigger_id,
          payload: snapshot?.payload ?? {},
          dry_run: true,
        },
        token ?? undefined,
      );
      setRerunResult(res);
    } catch (e) {
      setRerunError(e instanceof Error ? e.message : "Test run failed");
    } finally {
      setRerunning(false);
    }
  }, [detailFor, detail, token]);

  const total = useMemo(
    () =>
      counts == null
        ? null
        : Object.values(counts).reduce((a, b) => a + (b ?? 0), 0),
    [counts],
  );

  const hasActiveFilter =
    filter !== "all" || !!workflow || !!project || debouncedSearch.trim() !== "";

  return (
    <>
      <Helmet>
        <title>Executions | Workflows | Portal</title>
      </Helmet>

      <div className="space-y-6">
        <PageHeader
          title="Executions"
          description="Recent workflow runs. Click a row to see the full result, node outputs, and error trace."
          icon={HistoryIcon}
          eyebrow="Operations"
        />

        {error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {/* One content panel: controls bar, status chips, table, pager. Every
            control below drives a server round-trip — nothing here narrows or
            reorders the page it was given. */}
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm("")}
              placeholder="Search by workflow, project, event id or error..."
              containerClassName="flex-1 min-w-[200px]"
              aria-label="Search executions"
              // BE = FE: the list 422s a search over 200 chars (lara-data-plane
              // workflows/api/v2/executions.py, list_executions — an inline
              // check, not grid_params.py); cap it here so the operator never
              // sees that 422 as an empty grid.
              maxLength={200}
            />
            <Select
              value={workflow ?? "__all__"}
              onValueChange={(v) =>
                setSearch({ workflow: v === "__all__" ? undefined : v })
              }
            >
              <SelectTrigger className="w-[140px]" aria-label="Workflow filter">
                <SelectValue className="truncate" placeholder="All workflows" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All workflows</SelectItem>
                {workflowKeys.map((k) => (
                  <SelectItem key={k.workflow_key} value={k.workflow_key}>
                    {k.name || k.workflow_key}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={project ?? "__all__"}
              onValueChange={(v) =>
                setSearch({ project: v === "__all__" ? undefined : v })
              }
            >
              <SelectTrigger className="w-[140px]" aria-label="Project filter">
                <SelectValue className="truncate" placeholder="All projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All projects</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {workflow || project ? (
              <button
                type="button"
                onClick={() =>
                  setSearch({ workflow: undefined, project: undefined })
                }
                className="text-xs text-muted-foreground underline hover:text-foreground"
              >
                Clear
              </button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
              aria-label="Refresh"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Status chips — the status filter, as a second row under the
              controls bar. Counts are server-scoped to the other filters. */}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSearch({ status: undefined })}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                filter === "all"
                  ? "bg-foreground text-background border-foreground"
                  : "bg-muted text-muted-foreground border-transparent hover:bg-muted/80"
              }`}
            >
              All
              <span className="ml-2 opacity-70">{total ?? "—"}</span>
            </button>
            {STATUS_ORDER.map((status) => {
              const Icon = STATUS_ICON[status];
              const count = counts == null ? "—" : (counts[status] ?? 0);
              const active = filter === status;
              return (
                <button
                  type="button"
                  key={status}
                  onClick={() => setSearch({ status })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                    active
                      ? STATUS_CHIP_CLASS[status]
                      : "bg-muted text-muted-foreground border-transparent hover:bg-muted/80"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {STATUS_LABEL[status]}
                  <span className="opacity-70">{count}</span>
                </button>
              );
            })}
          </div>
          {counts === null && (
            <p
              className="-mt-2 mb-3 text-xs text-muted-foreground"
              data-testid="executions-counts-unavailable"
            >
              Counts are unavailable right now. Refresh to try again.
            </p>
          )}

          {isLoading && rows.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 && error ? (
            // The fetch failed (e.g. a 504 when the read hit its statement
            // timeout): the Alert above says why. "No executions yet" here
            // would claim the org has none.
            null
          ) : rows.length === 0 ? (
            <div className="py-12 text-center">
              <Clock className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              {/* Past page 1 an empty page means "no more", not "none": without
                  a count the pager offers Next on any full page, so the last
                  full page leads here. */}
              <h3 className="mb-2 text-lg font-medium">
                {pagination.page > 1
                  ? "No more executions"
                  : hasActiveFilter
                    ? "No runs match your search"
                    : "No executions yet"}
              </h3>
              <p className="mb-4 text-muted-foreground">
                {pagination.page > 1
                  ? "Go back a page to see the most recent ones."
                  : hasActiveFilter
                    ? "Try a different search term or filter."
                    : "Subscriptions will populate this list once they fire."}
              </p>
            </div>
          ) : (
            <ScrollableTable>
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead
                      className="w-[170px]"
                      direction={getSortDirection("started_at")}
                      onSort={() => requestSort("started_at")}
                    >
                      Started
                    </SortableTableHead>
                    <SortableTableHead
                      direction={getSortDirection("workflow_key")}
                      onSort={() => requestSort("workflow_key")}
                    >
                      Workflow
                    </SortableTableHead>
                    <SortableTableHead
                      className="w-[160px]"
                      direction={getSortDirection("project_name")}
                      onSort={() => requestSort("project_name")}
                    >
                      Project
                    </SortableTableHead>
                    <SortableTableHead
                      className="w-[120px]"
                      direction={getSortDirection("status")}
                      onSort={() => requestSort("status")}
                    >
                      Status
                    </SortableTableHead>
                    <SortableTableHead
                      className="w-[170px]"
                      direction={getSortDirection("completed_at")}
                      onSort={() => requestSort("completed_at")}
                    >
                      Completed
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled>
                      Error
                    </SortableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer"
                      onClick={() => openDetail(row)}
                    >
                      <TableCell className="text-xs whitespace-nowrap">
                        {formatTs(row.started_at_utc)}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="font-medium">
                          {row.workflow_key ?? (
                            <span className="text-muted-foreground italic">
                              inline
                            </span>
                          )}
                        </div>
                        {row.trigger_id ? (
                          <div className="text-muted-foreground font-mono truncate max-w-[260px]">
                            {row.trigger_id}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-xs">
                        {row.project_name ? (
                          <Badge variant="secondary" className="font-normal">
                            {row.project_name}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={row.status} />
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                        {formatTs(row.completed_at_utc)}
                      </TableCell>
                      <TableCell className="text-xs max-w-[420px] truncate text-rose-600">
                        {row.error_message ?? ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollableTable>
          )}
          {/* Outside the table/empty ternary on purpose: a page past the end
              shows the empty state AND the pager, so the operator can step
              back. */}
          {pagination.totalCount > 0 && (
            <div className="mt-4 border-t pt-4">
              <Pagination
                showSummary={counts != null}
                // Until the count lands (or if it failed) the total is the
                // rows+1 estimate above, so there is no real last page to
                // jump to.
                showFirstLast={counts != null}
                page={pagination.page}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                pageSize={pagination.pageSize}
                pageSizeOptions={pageSizeOptions}
                startIndex={startIndex}
                endIndex={endIndex}
                canGoPrev={canGoPrev}
                canGoNext={canGoNext}
                onPageChange={goToPage}
                onPageSizeChange={setPageSize}
                onFirstPage={firstPage}
                onLastPage={lastPage}
                onPrevPage={prevPage}
                onNextPage={nextPage}
              />
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={detailFor !== null}
        onOpenChange={(o) => {
          if (!o) closeDrawer();
        }}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-mono text-base break-all">
              {detailFor ? <StatusBadge status={detailFor.status} /> : null}
              {detailFor?.id}
            </DialogTitle>
            <DialogDescription>
              {detailFor ? (
                <>
                  Trigger {detailFor.trigger_id ?? "—"} · started{" "}
                  {formatTs(detailFor.started_at_utc) || "—"}
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading run…
            </div>
          ) : detail ? (
            <div className="space-y-4">
              {/* Re-run as test — replay this run as a dry-run (WS6). */}
              {detailFor?.workflow_key && detailFor.trigger_id ? (
                <div className="rounded-md border border-violet-200 bg-violet-50/40 p-3 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">
                        Re-run as test
                      </span>{" "}
                      — replay this run against the current published version
                      with no side effects (writes simulated).
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={runReTest}
                      disabled={rerunning}
                      className="flex-shrink-0"
                    >
                      {rerunning ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <FlaskConical className="h-4 w-4 mr-2" />
                      )}
                      Re-run as test
                    </Button>
                  </div>
                  {rerunError ? (
                    <p className="text-[11px] text-rose-600">{rerunError}</p>
                  ) : null}
                  {rerunResult ? (
                    <div className="border-t pt-2">
                      <DryRunResult result={rerunResult} />
                    </div>
                  ) : null}
                </div>
              ) : null}

              {detail.error ? (
                <div>
                  <div className="text-xs font-medium mb-1">Error</div>
                  <pre className="text-xs bg-destructive/10 text-destructive p-3 rounded whitespace-pre-wrap break-all max-h-[240px] overflow-auto">
                    {detail.error}
                  </pre>
                </div>
              ) : null}

              {detail.final_node_id || detail.final_exit ? (
                <div className="text-xs text-muted-foreground">
                  Final node{" "}
                  <code className="bg-muted px-1.5 py-0.5 rounded">
                    {detail.final_node_id ?? "—"}
                  </code>{" "}
                  · exit{" "}
                  <code className="bg-muted px-1.5 py-0.5 rounded">
                    {detail.final_exit ?? "—"}
                  </code>{" "}
                  · steps {detail.steps_taken}
                </div>
              ) : null}

              {detail.suspended_node_id ? (
                <Alert>
                  <Pause className="h-4 w-4" />
                  <AlertDescription>
                    Suspended at{" "}
                    <code className="font-mono">{detail.suspended_node_id}</code>
                    {detail.suspend_reason ? ` — ${detail.suspend_reason}` : ""}
                  </AlertDescription>
                </Alert>
              ) : null}

              <div>
                <div className="text-xs font-medium mb-1">Node outputs</div>
                <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-[360px]">
                  {JSON.stringify(detail.node_outputs ?? {}, null, 2)}
                </pre>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  const known = (STATUS_ORDER as readonly string[]).includes(status)
    ? (status as ExecutionListStatus)
    : null;
  if (!known) {
    return (
      <Badge variant="secondary" className="font-normal">
        {status}
      </Badge>
    );
  }
  const Icon = STATUS_ICON[known];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_CHIP_CLASS[known]}`}
    >
      <Icon className="h-3 w-3" />
      {STATUS_LABEL[known]}
    </span>
  );
}

function formatTs(ts: string | null): string {
  if (!ts) return "";
  // Backend hands back ISO strings; render in the viewer's locale so
  // operators see times in their own tz.
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString();
}
